import React from "react";
import styles from "./HotlineQuestionnaireNotice.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { IPendingHotlineQuestionnaire } from "../../services/types/user.types";

/**
 * Tells the therapist this patient owes the hotline questionnaire.
 *
 * One component for both places it appears — the incoming-call modal and the appointment details
 * modal — because it is the same fact arriving by two routes, and two lookalike banners drift
 * apart the first time somebody edits one of them.
 *
 * Renders nothing when nothing is owed, which is the answer for almost every patient. Callers can
 * mount it unconditionally rather than each repeating the null check.
 */
interface IHotlineQuestionnaireNoticeProps {
  pending: IPendingHotlineQuestionnaire | null | undefined;
  /** Tightens the padding where it sits inside an already-dense modal. */
  compact?: boolean;
}

/**
 * The reason decides the wording. Unknown values fall back to the returning-caller text rather
 * than rendering a blank strip: the server owns this enum and may gain a member before the
 * therapist app is redeployed.
 */
const REASON_KEYS: Record<string, string> = {
  ReturningCaller: "HOTLINE_QUESTIONNAIRE_ALERT_RETURNING",
  Periodic: "HOTLINE_QUESTIONNAIRE_ALERT_PERIODIC",
  LastContact: "HOTLINE_QUESTIONNAIRE_ALERT_LAST_CONTACT",
};

const HotlineQuestionnaireNotice: React.FC<IHotlineQuestionnaireNoticeProps> = ({
  pending,
  compact = false,
}) => {
  if (!pending) return null;

  const reasonKey = REASON_KEYS[pending.reason] ?? REASON_KEYS.ReturningCaller;

  // The last contact of a course is the one case the spec wants filled BEFORE the session rather
  // than by the end of it, so it reads as an instruction and not as a note.
  const urgent = pending.isLastContact || pending.reason === "LastContact";

  return (
    <div
      className={`${styles.notice} ${urgent ? styles.urgent : ""} ${compact ? styles.compact : ""}`}
      role="status"
    >
      <span className={styles.icon} aria-hidden="true">
        {urgent ? "!" : "i"}
      </span>
      <div className={styles.body}>
        <div className={styles.title}>{pending.name}</div>
        <div className={styles.text}>{t(reasonKey)}</div>

        {/* Filling it WITH the patient is the point of the alert -- telling a therapist something
            is missing and then making them go and find it is most of the way to it not happening.
            New tab, so whatever they were in the middle of survives. */}
        {pending.link && (
          <a
            className={styles.action}
            href={pending.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("HOTLINE_QUESTIONNAIRE_ALERT_FILL_NOW")}
          </a>
        )}
      </div>
    </div>
  );
};

export default HotlineQuestionnaireNotice;
